/* Breakdown: where Revenue (or, by Account, the AI's chats) came from. By Seller
   includes "Not sold in chat" so the shares add up to Revenue; by Type lists every
   revenue type on its own (never an "Other N" row). Rows scroll. */

import { useTranslation } from 'react-i18next';
import { C } from '../../components/ChatAssistant/compact/tokens';
import { font as f } from '../../components/ChatAssistant/compact/style';
import { SELLERS, fmt, pick, typeColor, usd } from '../../components/Statistics/unified/model';
import { periodRange, useDash, useSource } from '../context';
import type { WidgetOf } from '../model';
import { BodyNote, WidgetFrame, type FrameProps } from '../WidgetFrame';
import { bodySize, useDataState } from '../frameUtils';
import { SHADES, sellerFill, useMetaText } from './shared';

interface Row { id: string; label: string; value: number; color: string }

export function Breakdown(p: FrameProps & { widget: WidgetOf<'split'> }) {
  const { t } = useTranslation('dashboardWidgets');
  const { t: ts } = useTranslation('statsUnified');
  const { today, money, accountId } = useDash();
  const meta = useMetaText();
  const state = useDataState();
  const c = p.widget.cfg;
  // One Account picked: "by Account" has nothing to break down.
  const by = accountId && c.by === 'account' ? 'seller' : c.by;
  const chats = by === 'account' && c.metric === 'chats';
  const range = periodRange(c.per, today);
  const stats = useSource('stats', chats ? null : range);
  const chat = useSource('chat', chats ? range : null);
  const src = chats ? chat : stats;

  let rows: Row[] = [];
  if (!chats && stats.data) {
    const m = stats.data.money;
    if (by === 'seller') rows = SELLERS.map((k) => ({ id: k, label: ts(`sellers.${k}`), value: pick(m.sellers[k], money), color: sellerFill(k) }));
    else if (by === 'type') rows = m.revenue.by_type.map((x) => ({ id: x.type, label: ts(`types.${x.type}`, { defaultValue: x.type }), value: pick(x, money), color: typeColor(x.type) }));
    else rows = m.accounts.map((a, i) => ({ id: a.account_id, label: a.name, value: pick(a.revenue, money), color: SHADES[i % SHADES.length] ?? C.purple }));
  } else if (chats && chat.data) {
    rows = chat.data.accounts.map((a, i) => ({ id: a.account_id, label: a.name, value: a.chats_activated, color: SHADES[i % SHADES.length] ?? C.purple }));
  }
  rows = rows.filter((r) => r.value > 0 || by === 'seller').sort((a, b) => b.value - a.value);
  const total = rows.reduce((a, r) => a + r.value, 0);
  const peak = Math.max(1, ...rows.map((r) => r.value));
  const value = (v: number) => (chats ? fmt(v) : usd(v));
  const { width } = bodySize(p);
  const labelW = width < 340 ? 74 : 104;
  const note = state({ error: src.error, noAccess: src.noAccess, hasData: !!src.data, reload: src.reload });

  return (
    <WidgetFrame {...p} meta={meta.join([meta.period(c.per)])}>
      {note ?? (total <= 0 ? (
        <BodyNote>{t('split.empty')}</BodyNote>
      ) : (
        <div className="dbw-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {c.style === 'bar' && (
            <span style={{ flex: 'none', display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden', gap: 2, marginBottom: 6 }}>
              {rows.filter((r) => r.value > 0).map((r) => <span key={r.id} title={`${r.label} · ${value(r.value)}`} style={{ width: `${(r.value / total) * 100}%`, background: r.color }} />)}
            </span>
          )}
          {rows.map((r) => (
            <div key={r.id} style={{ height: 20, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, font: f(400, 12.5), color: C.body }}>
              <span style={{ width: 8, height: 8, flex: 'none', borderRadius: 2, background: r.color }} />
              <span title={r.label} style={{ width: labelW, flex: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
              <span style={{ flex: 1, height: 6, background: C.border, borderRadius: 99, overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${(r.value / peak) * 100}%`, background: r.color, borderRadius: 99 }} />
              </span>
              <span style={{ width: 60, flex: 'none', textAlign: 'right', font: f(600, 12.5), color: C.text }}>{value(r.value)}</span>
              <span style={{ width: 32, flex: 'none', textAlign: 'right', color: C.muted }}>{Math.round((r.value / total) * 100)}%</span>
            </div>
          ))}
        </div>
      ))}
    </WidgetFrame>
  );
}
